/**
 * Skeleton - Componentes de carga
 *
 * Muestra placeholders animados mientras se carga el contenido.
 * Reduce la percepcion de espera y ansiedad.
 */

import { cn } from '@/core/utils';

interface SkeletonProps {
  className?: string;
}

/**
 * Skeleton base - linea o bloque animado
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse bg-slate-800 rounded',
        className
      )}
    />
  );
}

/**
 * Skeleton para texto - simula una linea de texto
 */
export function SkeletonText({ className, lines = 1 }: SkeletonProps & { lines?: number }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'
          )}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton para avatar/icono circular
 */
export function SkeletonCircle({ className, size = 'md' }: SkeletonProps & { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  return (
    <Skeleton className={cn('rounded-full', sizes[size], className)} />
  );
}

/**
 * Skeleton para una card de protocolo
 */
export function SkeletonProtocolCard() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 flex-1">
          <SkeletonCircle size="sm" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
        <Skeleton className="h-6 w-16" />
      </div>

      {/* Subtasks */}
      <div className="space-y-2 pl-2">
        <div className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded" />
          <Skeleton className="h-4 flex-1" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded" />
          <Skeleton className="h-4 w-4/5" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton para el dashboard completo
 */
export function SkeletonDashboard() {
  return (
    <div className="space-y-6">
      {/* Header con tabs */}
      <div className="flex gap-2">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>

      {/* Indicadores de progreso */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-900 rounded-xl p-4 space-y-2">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-6 w-full" />
        </div>
        <div className="bg-slate-900 rounded-xl p-4 space-y-2">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-6 w-full" />
        </div>
        <div className="bg-slate-900 rounded-xl p-4 space-y-2">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-6 w-full" />
        </div>
      </div>

      {/* Cards de protocolos */}
      <div className="space-y-4">
        <SkeletonProtocolCard />
        <SkeletonProtocolCard />
        <SkeletonProtocolCard />
      </div>
    </div>
  );
}

/**
 * Skeleton para la vista de stats
 */
export function SkeletonStats() {
  return (
    <div className="space-y-6">
      {/* Selector de periodo */}
      <Skeleton className="h-12 w-full rounded-lg" />

      {/* Metricas principales */}
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-slate-800/50 rounded-lg p-4 space-y-2">
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ))}
      </div>

      {/* Grafico de progreso por capas */}
      <div className="bg-slate-800/50 rounded-lg p-4 space-y-4">
        <Skeleton className="h-4 w-1/3" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-1/4" />
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton para checklist
 */
export function SkeletonChecklist() {
  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-16 rounded-lg flex-shrink-0" />
        ))}
      </div>

      {/* Progress header */}
      <div className="bg-slate-800/50 rounded-lg p-4 flex items-center justify-between">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-8 w-20" />
      </div>

      {/* Items */}
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
            <Skeleton className="w-5 h-5 rounded" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
    </div>
  );
}
